import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { postJournal } from '../../api/journal';
import { ACCOUNTS, payableAccountForSupplierType, rateFor } from '../../api/accounts';
import { getClientDepositBalance, getClientArBalance } from '../../api/receiptAllocations';
import BranchSelectModal from '../../components/common/BranchSelectModal';

const PAYABLE_ACCT_IDS = [ACCOUNTS.AP_SUPPLIERS, ACCOUNTS.VISA_PAYABLE, ACCOUNTS.AIRLINE_PAYABLE, ACCOUNTS.HOTEL_LAND_PAYABLE, ACCOUNTS.COMMISSION_PAYABLE];
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';

export default function PaymentVoucherForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, branchId, role } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const isSadmin = role === 'sadmin';
  const billId = searchParams.get('bill_id') || '';

  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  // 'supplier' (pay a supplier — existing behavior, default) or 'client' (refund a client's
  // deposit and/or AR overpayment — the missing "refund payment" half of proper accounting).
  const [payeeType, setPayeeType] = useState('supplier');
  const [clientLabel, setClientLabel] = useState('');
  const [clientDepositBalance, setClientDepositBalance] = useState(null);
  const [clientArBalance, setClientArBalance] = useState(null);
  const [form, setForm] = useState({
    voucher_date: new Date().toISOString().slice(0, 10),
    paid_to: searchParams.get('payee') || '',
    supplier_id: '',
    client_id: '',
    refund_amount: '',
    paid_from_account_id: '',
    // Coming from "Pay This Bill" (BillDetail.js) — bills post DR Expense / CR Other
    // Payables (2060) at creation, so paying one off settles that same payable account.
    expense_account_id: searchParams.get('bill_id') ? ACCOUNTS.OTHER_PAYABLES : '',
    amount: searchParams.get('amount') || '',
    currency_id: '',
    exchange_rate: '1',   // payment rate (today's market rate, → LKR)
    booked_rate: '',      // rate the payable was originally booked at (→ LKR)
    payment_mode: '',
    bank_reference: '',
    description: searchParams.get('desc') || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchConfirmed, setBranchConfirmed] = useState(false);
  const [supplierBalance, setSupplierBalance] = useState(null);
  const [openItems, setOpenItems] = useState({ bookings: [], tickets: [], visas: [] });
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // If the selected "Paid From" account is a dedicated single-party account (e.g. a
  // supplier's own float, like Rajabdeen Haji's), every prior posting to it will already
  // carry the same party tag — look that up so drawing on it here tags the same party
  // automatically, instead of every use of that account going untagged. A shared bank/cash
  // account has no consistent party across its history, so this naturally stays null there.
  const [paidFromParty, setPaidFromParty] = useState(null);

  useEffect(() => {
    supabase.from('chart_of_accounts').select('id, account_code, account_name, is_payment_source, account_types(category)').eq('is_active', true).order('account_code')
      .then(({ data }) => setAccounts((data || []).map(a => ({ ...a, category: a.account_types?.category || '' }))));
    // Sub-suppliers (e.g. Arabian Lux under Rajabdeen Haji) are never paid directly —
    // we owe the parent, so only top-level suppliers can be paid here.
    supabase.from('suppliers').select('id, name, supplier_type').eq('is_active', true).is('parent_supplier_id', null).order('name')
      .then(({ data }) => setSuppliers(data || []));
    supabase.from('currencies').select('id, code, name').eq('is_active', true).order('code')
      .then(({ data }) => { setCurrencies(data || []); const lkr = (data || []).find(c => c.code === 'LKR'); if (lkr) set('currency_id', lkr.id); });
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name')
      .then(({ data }) => setBranches(data || []));
  }, []);

  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  useEffect(() => {
    let cancelled = false;
    if (!form.paid_from_account_id) { setPaidFromParty(null); return; }
    supabase.from('journal_lines').select('party_type, party_id')
      .eq('account_id', form.paid_from_account_id).not('party_id', 'is', null).limit(50)
      .then(({ data }) => {
        if (cancelled) return;
        const distinct = new Set((data || []).map(l => `${l.party_type}|${l.party_id}`));
        if (distinct.size === 1) {
          const [party_type, party_id] = distinct.values().next().value.split('|');
          setPaidFromParty({ party_type, party_id });
        } else {
          setPaidFromParty(null);
        }
      });
    return () => { cancelled = true; };
  }, [form.paid_from_account_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadSupplierData = async (supplierId) => {
    setLoadingItems(true);
    setSupplierBalance(null);
    setOpenItems({ bookings: [], tickets: [], visas: [] });
    setSelectedItem(null);

    const [{ data: lines }, { data: bkSuppliers }, { data: tickets }, { data: visas }] = await Promise.all([
      supabase.from('journal_lines').select('base_debit, base_credit, journal_entries!inner(status)')
        .in('account_id', PAYABLE_ACCT_IDS).eq('party_id', supplierId).eq('journal_entries.status', 'POSTED'),
      supabase.from('booking_suppliers')
        .select('*, bookings!inner(booking_ref, booking_status, clients(full_name))')
        .eq('supplier_id', supplierId).neq('payment_status', 'PAID').neq('bookings.booking_status', 'CANCELLED')
        .order('created_at'),
      supabase.from('tickets')
        .select('id, ticket_ref, ticket_number, pnr, cost_price, cost_currency:cost_currency_id(id, code), exchange_rate, clients(full_name)')
        .eq('supplier_id', supplierId).neq('payment_status', 'PAID').neq('ticket_status', 'CANCELLED')
        .order('created_at'),
      supabase.from('visas')
        .select('id, visa_ref, cost_price, cost_currency:cost_currency_id(id, code), exchange_rate, clients(full_name), visa_types(name)')
        .eq('supplier_id', supplierId).neq('payment_status', 'PAID')
        .order('created_at'),
    ]);

    const balance = (lines || []).reduce((s, l) => s + parseFloat(l.base_credit || 0) - parseFloat(l.base_debit || 0), 0);
    setSupplierBalance(balance);
    setOpenItems({ bookings: bkSuppliers || [], tickets: tickets || [], visas: visas || [] });
    setLoadingItems(false);
  };

  const loadClientData = async (clientId) => {
    setClientDepositBalance(null);
    setClientArBalance(null);
    const [dep, ar] = await Promise.all([getClientDepositBalance(clientId), getClientArBalance(clientId)]);
    setClientDepositBalance(dep);
    setClientArBalance(ar);
  };

  const applyItem = (type, item) => {
    const remaining = type === 'BOOKING_SUPPLIER'
      ? parseFloat(item.cost_amount || 0) - parseFloat(item.amount_paid || 0)
      : parseFloat(item.cost_price || 0);
    const currId = type === 'BOOKING_SUPPLIER' ? item.currency_id : item.cost_currency?.id;
    const currCode = type === 'BOOKING_SUPPLIER' ? 'LKR' : item.cost_currency?.code;
    const bookedRate = type === 'BOOKING_SUPPLIER' ? '1' : String(item.exchange_rate || '1');
    const clientName = type === 'BOOKING_SUPPLIER' ? item.bookings?.clients?.full_name : item.clients?.full_name;
    const ref = type === 'BOOKING_SUPPLIER'
      ? `${item.bookings?.booking_ref}${item.supplier_ref ? ' · ' + item.supplier_ref : ''}`
      : type === 'TICKET' ? (item.ticket_number || item.pnr || item.ticket_ref)
      : item.visa_ref;
    setForm(f => ({
      ...f,
      amount: String(remaining),
      currency_id: currId || f.currency_id,
      booked_rate: currCode === 'LKR' ? '' : bookedRate,
      description: `Payment: ${ref}${clientName ? ' · ' + clientName : ''}`,
    }));
    setSelectedItem({ type, id: type === 'BOOKING_SUPPLIER' ? item.id : item.id, ref });
  };

  const targetAccount = accounts.find(a => a.id === form.expense_account_id);
  const bankAccount = accounts.find(a => a.id === form.paid_from_account_id);
  const currency = currencies.find(c => c.id === form.currency_id);
  const isLKR = !currency || currency.code === 'LKR';
  const isPayable = targetAccount?.category === 'LIABILITY';
  const isOpeningBalance = (form.payment_mode || '').toUpperCase() === 'OPENING BALANCE';
  const isClientRefund = payeeType === 'client';

  // A client's refund is drawn first from their unused Client Deposits (2010), then any
  // remainder from an AR (1030) overpayment — both are pre-existing accounts, this isn't a
  // new "AR overpayment" ledger, just the existing AR balance for this client being negative.
  const clientRefundSplit = () => {
    const depAvail = Math.max(0, clientDepositBalance || 0);
    const arAvail = clientArBalance < 0 ? -clientArBalance : 0;
    const amt = parseFloat(form.refund_amount) || 0;
    const depAmt = Math.min(amt, depAvail);
    const arAmt = Math.min(amt - depAmt, arAvail);
    return { depAmt, arAmt, total: depAvail + arAvail };
  };

  // Build the journal lines (in LKR base) for the current form. Modes:
  //  - Client refund: DR Client Deposits and/or DR AR (party: client) / CR Bank — paying
  //    back an unused deposit and/or an invoice overpayment. Always LKR base, no FX legs,
  //    since both source accounts already carry LKR-converted balances.
  //  - Opening Balance (supplier): CR Payable / DR Opening Balance Equity — seeds AP
  //  - Settling a supplier payable (LIABILITY): clear AP at the booked rate, pay
  //    bank at today's rate, and book the FX difference as gain/loss.
  //  - Direct expense (EXPENSE): DR Expense / CR Bank at the payment rate.
  const buildLines = () => {
    // Tag the "Paid From" credit line with the account's own party, when it has one (a
    // shared bank/cash account never does, so this is a no-op there).
    const paidFromTag = paidFromParty ? { party_type: paidFromParty.party_type, party_id: paidFromParty.party_id } : {};
    if (isClientRefund) {
      const { depAmt, arAmt } = clientRefundSplit();
      const lines = [];
      if (depAmt > 0) lines.push({ account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: depAmt, credit_amount: 0, party_type: 'client', party_id: form.client_id, description: `Deposit refund - ${form.paid_to || 'Client'}` });
      if (arAmt > 0) lines.push({ account_id: ACCOUNTS.AR, debit_amount: arAmt, credit_amount: 0, party_type: 'client', party_id: form.client_id, description: `AR overpayment refund - ${form.paid_to || 'Client'}` });
      lines.push({ account_id: form.paid_from_account_id, debit_amount: 0, credit_amount: depAmt + arAmt, description: `Refund - ${form.payment_mode || 'Bank'}`, ...paidFromTag });
      return { lines, basePaid: depAmt + arAmt };
    }
    const amt = parseFloat(form.amount) || 0;
    const payRate = isLKR ? 1 : (parseFloat(form.exchange_rate) || 1);
    const basePaid = amt * payRate;
    const lines = [];
    if (isOpeningBalance) {
      // Seeds the supplier's AP balance — no bank movement
      const payableAccId = form.expense_account_id || ACCOUNTS.AP_SUPPLIERS;
      lines.push({ account_id: ACCOUNTS.OPENING_BALANCE_EQUITY, debit_amount: basePaid, credit_amount: 0, description: `Opening balance - ${form.paid_to || 'Supplier'}` });
      lines.push({ account_id: payableAccId, debit_amount: 0, credit_amount: basePaid, party_type: form.supplier_id ? 'supplier' : null, party_id: form.supplier_id || null, description: `Opening balance AP - ${form.paid_to || 'Supplier'}` });
    } else if (isPayable) {
      const bookedRate = isLKR ? 1 : (parseFloat(form.booked_rate) || payRate);
      const baseBooked = amt * bookedRate;
      lines.push({ account_id: form.expense_account_id, debit_amount: baseBooked, credit_amount: 0, party_type: form.supplier_id ? 'supplier' : null, party_id: form.supplier_id || null, description: `AP settled - ${form.paid_to || 'Supplier'}` });
      lines.push({ account_id: form.paid_from_account_id, debit_amount: 0, credit_amount: basePaid, description: `Payment - ${form.payment_mode || 'Bank'}`, ...paidFromTag });
      const fxDiff = basePaid - baseBooked; // paid more than booked = loss
      if (Math.abs(fxDiff) > 0.005) {
        if (fxDiff > 0) lines.push({ account_id: ACCOUNTS.FX_LOSS, debit_amount: fxDiff, credit_amount: 0, description: 'FX loss on settlement' });
        else            lines.push({ account_id: ACCOUNTS.FX_GAIN, debit_amount: 0, credit_amount: -fxDiff, description: 'FX gain on settlement' });
      }
    } else {
      lines.push({ account_id: form.expense_account_id, debit_amount: basePaid, credit_amount: 0, description: form.description || 'Expense' });
      lines.push({ account_id: form.paid_from_account_id, debit_amount: 0, credit_amount: basePaid, description: `Payment - ${form.payment_mode || 'Bank'}`, ...paidFromTag });
    }
    return { lines, basePaid };
  };

  const handleSubmit = async () => {
    if (isClientRefund) {
      const amt = parseFloat(form.refund_amount) || 0;
      const { total } = clientRefundSplit();
      if (!form.client_id || amt <= 0) {
        return setError('Select a client and a refund amount');
      }
      if (amt > total + 0.01) {
        return setError(`Refund amount (${amt.toLocaleString()}) exceeds the client's available refundable balance (${total.toLocaleString()})`);
      }
      if (!form.paid_from_account_id) return setError('Bank account is required');
    } else {
      if (!form.expense_account_id || !form.amount || !form.currency_id) {
        return setError('Payable/expense account, amount and currency are required');
      }
      if (!isOpeningBalance && !form.paid_from_account_id) {
        return setError('Bank account is required');
      }
    }
    setSaving(true); setError('');
    try {
      const { lines, basePaid } = buildLines();
      // Posted in LKR base (rate 1) — the AP-clear and FX legs use different rates,
      // so we compute the LKR amounts ourselves rather than rely on one entry rate.
      const je = await postJournal({
        date: form.voucher_date,
        description: form.description || `${isClientRefund ? 'Refund to' : 'Payment to'} ${form.paid_to || (isClientRefund ? 'Client' : 'Supplier')}`,
        reference_type: 'PAYMENT_VOUCHER',
        currency_id: null,
        exchange_rate: 1,
        created_by: username,
        lines,
        branch_id: selectedBranchId || null,
      });

      const yy = String(new Date().getFullYear()).slice(-2);
      const isCash = (form.payment_mode || '').toUpperCase() === 'CASH';
      const pvPrefix = isClientRefund ? `CRV-${yy}` : (isCash ? `CPV-${yy}` : `BPV-${yy}`);
      const { count: pvCount } = await supabase.from('payment_vouchers').select('*', { count: 'exact', head: true }).ilike('voucher_number', `${pvPrefix}-%`);
      const voucher_number = `${pvPrefix}-${String((pvCount || 0) + 1).padStart(4, '0')}`;
      const lkrId = currencies.find(c => c.code === 'LKR')?.id || form.currency_id;
      const { depAmt, arAmt } = isClientRefund ? clientRefundSplit() : { depAmt: 0, arAmt: 0 };
      const refundDisplayAccount = arAmt > 0 && depAmt <= 0 ? ACCOUNTS.AR : ACCOUNTS.CLIENT_DEPOSITS;
      const { data: pv, error: pvErr } = await supabase.from('payment_vouchers').insert({
        voucher_number,
        voucher_date: form.voucher_date,
        paid_to: form.paid_to,
        supplier_id: isClientRefund ? null : (form.supplier_id || null),
        client_id: isClientRefund ? form.client_id : null,
        paid_from_account_id: form.paid_from_account_id,
        expense_account_id: isClientRefund ? refundDisplayAccount : form.expense_account_id,
        amount: isClientRefund ? basePaid : parseFloat(form.amount),
        currency_id: isClientRefund ? lkrId : form.currency_id,
        exchange_rate: isClientRefund ? 1 : (isLKR ? 1 : (parseFloat(form.exchange_rate) || 1)),
        base_amount: basePaid,
        payment_mode: form.payment_mode,
        bank_reference: form.bank_reference,
        description: form.description,
        voucher_status: 'PAID',
        reference_type: billId ? 'bill' : null,
        reference_id: billId || null,
        journal_entry_id: je.id,
        created_by: username,
        branch_id: selectedBranchId || null,
      }).select().single();

      if (pvErr) throw pvErr;

      await supabase.from('journal_entries').update({ reference_id: pv.id }).eq('id', je.id);

      // Mark the bill PAID once its payments cover the full amount (partial payments
      // leave it UNPAID so BillDetail's balance/"Pay This Bill" flow keeps working).
      if (billId) {
        const [{ data: billRow }, { data: pvRows }] = await Promise.all([
          supabase.from('bills').select('amount').eq('id', billId).single(),
          supabase.from('payment_vouchers').select('amount').eq('reference_type', 'bill').eq('reference_id', billId),
        ]);
        const totalPaid = (pvRows || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        if (billRow && totalPaid >= parseFloat(billRow.amount) - 0.01) {
          await supabase.from('bills').update({ status: 'PAID' }).eq('id', billId);
        }
      }

      // Update the source document's payment status
      if (selectedItem) {
        const paidAmt = parseFloat(form.amount) || 0;
        if (selectedItem.type === 'BOOKING_SUPPLIER') {
          const { data: bs } = await supabase.from('booking_suppliers').select('cost_amount, amount_paid').eq('id', selectedItem.id).single();
          if (bs) {
            const newPaid = parseFloat(bs.amount_paid || 0) + paidAmt;
            const newStatus = newPaid >= parseFloat(bs.cost_amount) ? 'PAID' : 'PARTIAL';
            await supabase.from('booking_suppliers').update({ amount_paid: newPaid, payment_status: newStatus }).eq('id', selectedItem.id);
          }
        } else if (selectedItem.type === 'TICKET') {
          await supabase.from('tickets').update({ payment_status: 'PAID' }).eq('id', selectedItem.id);
        } else if (selectedItem.type === 'VISA') {
          await supabase.from('visas').update({ payment_status: 'PAID' }).eq('id', selectedItem.id);
        }
      }

      setSaved({ ...pv, je_number: je.entry_number });
    } catch (err) {
      setError(err.message || 'Error saving voucher');
      setSaving(false);
    }
  };

  if (saved) return (
    <div>
      <div className="page-header"><div className="page-title">Payment Voucher Posted</div></div>
      <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{saved.voucher_number}</div>
        <div style={{ color: 'var(--text-muted)', marginBottom: 6 }}>Journal Entry: <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{saved.je_number}</span></div>
        <div style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Amount: {currency?.code} {parseFloat(saved.amount).toLocaleString()}</div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => navigate(`/vouchers/payment/${saved.id}`)}>Print / View</button>
          <button className="btn btn-secondary" onClick={() => { setSaved(null); setPayeeType('supplier'); setClientLabel(''); setClientDepositBalance(null); setClientArBalance(null); setForm({ voucher_date: new Date().toISOString().slice(0, 10), paid_to: '', supplier_id: '', client_id: '', refund_amount: '', paid_from_account_id: '', expense_account_id: '', amount: '', currency_id: '', exchange_rate: '1', booked_rate: '', payment_mode: '', bank_reference: '', description: '' }); setSaving(false); }}>+ New Payment Voucher</button>
          <button className="btn btn-secondary" onClick={() => navigate('/vouchers/payment')}>View All</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {!branchConfirmed && (
        <BranchSelectModal
          defaultBranchId={selectedBranchId || branchId}
          onConfirm={(id) => { setSelectedBranchId(id); setBranchConfirmed(true); }}
          onCancel={() => navigate(-1)}
        />
      )}
      <div className="page-header">
        <div><div className="page-title">Payment Voucher</div><div className="page-subtitle">Money going out — auto posts journal entry</div></div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Posting...' : 'Post Payment'}</button>
          </div>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className={`btn btn-sm ${payeeType === 'supplier' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setPayeeType('supplier'); setClientLabel(''); setForm(f => ({ ...f, client_id: '', refund_amount: '' })); setClientDepositBalance(null); setClientArBalance(null); }}>
          Pay Supplier
        </button>
        <button className={`btn btn-sm ${payeeType === 'client' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setPayeeType('client'); setForm(f => ({ ...f, supplier_id: '', expense_account_id: '', refund_amount: '' })); setSupplierBalance(null); setOpenItems({ bookings: [], tickets: [], visas: [] }); setSelectedItem(null); }}>
          Refund Client
        </button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, padding: '12px 16px', background: isClientRefund ? 'rgba(34,197,94,0.06)' : isOpeningBalance ? 'rgba(79,110,247,0.07)' : 'rgba(239,68,68,0.05)', borderRadius: 8, fontSize: 13 }}>
          <span style={{ color: isClientRefund ? '#16a34a' : isOpeningBalance ? 'var(--primary)' : 'var(--danger)', fontWeight: 600 }}>
            {isClientRefund ? 'CLIENT REFUND' : isOpeningBalance ? 'OPENING BALANCE' : 'PAYMENT OUT'}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>
            {isSadmin
              ? (isClientRefund
                  ? ' Cash refund to a client — DR Client Deposits and/or Accounts Receivable / CR Bank'
                  : isOpeningBalance
                  ? ' DR Opening Balance Equity / CR Accounts Payable — seeds supplier AP balance, no bank movement'
                  : isPayable
                  ? ' Settling a supplier payable — DR Accounts Payable (at booked rate) / CR Bank (at payment rate) / FX gain or loss'
                  : ' Direct expense — DR Expense Account / CR Bank Account')
              : (isClientRefund
                  ? ' Cash refund to a client'
                  : isOpeningBalance
                  ? ' Seeds this supplier’s opening balance — no bank movement'
                  : isPayable
                  ? ' Settling a supplier payable'
                  : ' Direct expense payment')}
          </span>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <input className="form-input" type="date" value={form.voucher_date} onChange={e => set('voucher_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Paid To</label>
            <input className="form-input" value={form.paid_to} onChange={e => set('paid_to', e.target.value)} placeholder="Payee name" />
          </div>
          {payeeType === 'supplier' && (
          <div className="form-group">
            <label className="form-label">Supplier (optional)</label>
            <TypeSearch
              options={suppliers}
              value={suppliers.find(s => s.id === form.supplier_id)?.name || ''}
              displayKey="name"
              placeholder="Type supplier name…"
              onSelect={s => { setForm(f => ({ ...f, supplier_id: s.id, paid_to: f.paid_to || s.name, expense_account_id: f.expense_account_id || payableAccountForSupplierType(s.supplier_type) })); loadSupplierData(s.id); }}
              onClear={() => { set('supplier_id', ''); setSupplierBalance(null); setOpenItems({ bookings: [], tickets: [], visas: [] }); setSelectedItem(null); }}
            />
          </div>
          )}
          {isClientRefund && (
          <div className="form-group">
            <label className="form-label">Client *</label>
            <TypeSearch
              fetchFn={async (q) => {
                const { data } = await supabase.from('clients')
                  .select('id, full_name, client_code')
                  .eq('is_active', true)
                  .or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`)
                  .order('full_name').limit(30);
                return data || [];
              }}
              value={clientLabel}
              displayKey="full_name"
              placeholder="Type client name…"
              onSelect={c => { setClientLabel(c.full_name); setForm(f => ({ ...f, client_id: c.id, paid_to: f.paid_to || c.full_name })); loadClientData(c.id); }}
              onClear={() => { setClientLabel(''); set('client_id', ''); setClientDepositBalance(null); setClientArBalance(null); }}
            />
          </div>
          )}
          {/* Client refundable balance + single refund amount input */}
          {isClientRefund && form.client_id && (
            <div className="form-group full-width" style={{ margin: 0 }}>
              {(clientDepositBalance !== null || clientArBalance !== null) && (
                <div style={{ marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '4px 10px', borderRadius: 20 }}>
                    Refundable Balance: LKR {clientRefundSplit().total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                    Deposits {(clientDepositBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    {clientArBalance < 0 ? ` + Overpaid invoices ${(-clientArBalance).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ''}
                  </span>
                </div>
              )}
              <div className="form-group" style={{ margin: 0, maxWidth: 260 }}>
                <label className="form-label">Refund Amount *</label>
                <input className="form-input" type="number" step="0.01" value={form.refund_amount}
                  onChange={e => set('refund_amount', e.target.value)} placeholder="0.00" style={{ fontSize: 16, fontWeight: 600 }} />
              </div>
              {(parseFloat(form.refund_amount) || 0) > clientRefundSplit().total && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>
                  This client has no refundable balance available{clientRefundSplit().total > 0 ? ` beyond LKR ${clientRefundSplit().total.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ''} — nothing will post until the amount is reduced.
                </div>
              )}
            </div>
          )}
          {/* Supplier balance + open items */}
          {payeeType === 'supplier' && form.supplier_id && (
            <div className="form-group full-width" style={{ margin: 0 }}>
              {supplierBalance !== null && (
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: supplierBalance > 0 ? '#dc2626' : '#16a34a', background: supplierBalance > 0 ? '#fef2f2' : '#f0fdf4', padding: '4px 10px', borderRadius: 20 }}>
                    Due: LKR {supplierBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  {selectedItem && <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>Selected: {selectedItem.ref}</span>}
                </div>
              )}
              {loadingItems && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Loading open items…</div>}
              {!loadingItems && (openItems.bookings.length > 0 || openItems.tickets.length > 0 || openItems.visas.length > 0) && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 4 }}>
                  {/* Package Bookings */}
                  {openItems.bookings.length > 0 && (
                    <div>
                      <div style={{ padding: '6px 12px', background: 'var(--bg)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Package Bookings</div>
                      {openItems.bookings.map(b => {
                        const remaining = parseFloat(b.cost_amount || 0) - parseFloat(b.amount_paid || 0);
                        return (
                          <div key={b.id} onClick={() => applyItem('BOOKING_SUPPLIER', b)}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderTop: '1px solid var(--border)', cursor: 'pointer', background: selectedItem?.id === b.id ? 'rgba(240,112,64,0.06)' : '', fontSize: 13 }}
                            onMouseEnter={e => { if (selectedItem?.id !== b.id) e.currentTarget.style.background = 'var(--bg)'; }}
                            onMouseLeave={e => { if (selectedItem?.id !== b.id) e.currentTarget.style.background = ''; }}>
                            <div>
                              <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{b.bookings?.booking_ref}</span>
                              {b.supplier_ref && <span style={{ marginLeft: 6, fontFamily: 'monospace', fontSize: 12, background: 'rgba(240,112,64,0.1)', padding: '1px 6px', borderRadius: 4 }}>{b.supplier_ref}</span>}
                              <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>{b.bookings?.clients?.full_name}</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: '#dc2626' }}>LKR {remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                              {parseFloat(b.amount_paid) > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{parseFloat(b.amount_paid).toLocaleString()} paid</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Tickets (FIFO) */}
                  {openItems.tickets.length > 0 && (
                    <div>
                      <div style={{ padding: '6px 12px', background: 'var(--bg)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: openItems.bookings.length > 0 ? '2px solid var(--border)' : 'none' }}>Tickets (FIFO)</div>
                      {openItems.tickets.map(t => (
                        <div key={t.id} onClick={() => applyItem('TICKET', t)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderTop: '1px solid var(--border)', cursor: 'pointer', background: selectedItem?.id === t.id ? 'rgba(240,112,64,0.06)' : '', fontSize: 13 }}
                          onMouseEnter={e => { if (selectedItem?.id !== t.id) e.currentTarget.style.background = 'var(--bg)'; }}
                          onMouseLeave={e => { if (selectedItem?.id !== t.id) e.currentTarget.style.background = ''; }}>
                          <div>
                            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{t.ticket_ref}</span>
                            {(t.ticket_number || t.pnr) && <span style={{ marginLeft: 6, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{t.ticket_number || t.pnr}</span>}
                            <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>{t.clients?.full_name}</span>
                          </div>
                          <div style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                            {t.cost_currency?.code} {parseFloat(t.cost_price || 0).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Visas */}
                  {openItems.visas.length > 0 && (
                    <div>
                      <div style={{ padding: '6px 12px', background: 'var(--bg)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: (openItems.bookings.length > 0 || openItems.tickets.length > 0) ? '2px solid var(--border)' : 'none' }}>Visas</div>
                      {openItems.visas.map(v => (
                        <div key={v.id} onClick={() => applyItem('VISA', v)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderTop: '1px solid var(--border)', cursor: 'pointer', background: selectedItem?.id === v.id ? 'rgba(240,112,64,0.06)' : '', fontSize: 13 }}
                          onMouseEnter={e => { if (selectedItem?.id !== v.id) e.currentTarget.style.background = 'var(--bg)'; }}
                          onMouseLeave={e => { if (selectedItem?.id !== v.id) e.currentTarget.style.background = ''; }}>
                          <div>
                            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{v.visa_ref}</span>
                            {v.visa_types?.name && <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: 12 }}>{v.visa_types.name}</span>}
                            <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>{v.clients?.full_name}</span>
                          </div>
                          <div style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                            {v.cost_currency?.code} {parseFloat(v.cost_price || 0).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!isClientRefund && (
          <div className="form-group">
            <label className="form-label">Currency *</label>
            <TypeSearch
              options={currencies}
              value={currencies.find(c => c.id === form.currency_id)?.code || ''}
              displayKey="code"
              placeholder="Type currency…"
              onSelect={c => set('currency_id', c.id)}
              onClear={() => set('currency_id', '')}
              renderOption={(item) => <><span style={{ fontWeight: 600 }}>{item.code}</span><span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>{item.name}</span></>}
            />
          </div>
          )}
          {!isClientRefund && (
          <div className="form-group">
            <label className="form-label">Amount *</label>
            <input className="form-input" type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" style={{ fontSize: 16, fontWeight: 600 }} />
          </div>
          )}
          {!isClientRefund && !isLKR && (
            <div className="form-group">
              <label className="form-label">Payment Rate → LKR <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(today's market rate)</span></label>
              <input className="form-input" type="number" step="0.0001" value={form.exchange_rate} onChange={e => set('exchange_rate', e.target.value)} placeholder="e.g. 340" />
            </div>
          )}
          {!isClientRefund && !isLKR && isPayable && (
            <div className="form-group">
              <label className="form-label">Booked Rate → LKR <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>(rate the cost was booked at)</span></label>
              <input className="form-input" type="number" step="0.0001" value={form.booked_rate} onChange={e => set('booked_rate', e.target.value)} placeholder="e.g. 320" />
            </div>
          )}
          {!isOpeningBalance && (
            <div className="form-group">
              <label className="form-label">Paid From (Bank/Cash Account) *</label>
              <TypeSearch
                options={accounts.filter(a => a.is_payment_source)}
                value={accounts.find(a => a.id === form.paid_from_account_id)?.account_name || ''}
                displayKey="account_name"
                placeholder="Type account name…"
                onSelect={a => set('paid_from_account_id', a.id)}
                onClear={() => set('paid_from_account_id', '')}
              />
            </div>
          )}
          {!isClientRefund && (
            <div className="form-group">
              <label className="form-label">
                {form.supplier_id ? 'Payable Account *' : 'Expense Account *'}
                <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>
                  {form.supplier_id
                    ? ' (auto-filled from supplier type — override if this supplier has a dedicated account)'
                    : ' (for direct overhead payments)'}
                </span>
              </label>
              <TypeSearch
                options={accounts.filter(a => (a.category === 'LIABILITY' && parseInt(a.account_code) >= 2000 && a.account_code !== '2010') || (a.category === 'EXPENSE' && parseInt(a.account_code) >= 6000))}
                value={accounts.find(a => a.id === form.expense_account_id)?.account_name || ''}
                displayKey="account_name"
                placeholder="Type account name…"
                onSelect={a => set('expense_account_id', a.id)}
                onClear={() => set('expense_account_id', '')}
              />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Payment Mode</label>
            <TypeSearch
              options={['Cash','Cheque','Bank Transfer','Card','Online','Opening Balance'].map(m => ({ id: m, label: m }))}
              value={form.payment_mode}
              displayKey="label"
              placeholder="Select payment mode…"
              onSelect={m => set('payment_mode', m.id)}
              onClear={() => set('payment_mode', '')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Bank Reference / Cheque No</label>
            <input className="form-input" value={form.bank_reference} onChange={e => set('bank_reference', e.target.value)} />
          </div>
          <div className="form-group full-width">
            <label className="form-label">Description / Narration</label>
            <textarea className="form-textarea" rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What is this payment for?" />
          </div>
        </div>

        {isSadmin && ((isClientRefund && bankAccount && clientRefundSplit().total > 0 && (parseFloat(form.refund_amount) || 0) > 0) || (!isClientRefund && targetAccount && bankAccount && form.amount)) && (
          <div style={{ marginTop: 20, padding: '14px 18px', background: 'var(--bg)', borderRadius: 8, fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Journal Preview (LKR)</div>
            <table style={{ width: '100%' }}>
              <thead><tr>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500, paddingBottom: 6 }}>Account</th>
                <th style={{ textAlign: 'right', color: 'var(--text-muted)', fontWeight: 500 }}>DR</th>
                <th style={{ textAlign: 'right', color: 'var(--text-muted)', fontWeight: 500 }}>CR</th>
              </tr></thead>
              <tbody>
                {buildLines().lines.map((l, i) => {
                  const acc = accounts.find(a => a.id === l.account_id);
                  return (
                    <tr key={i}>
                      <td style={{ padding: '4px 0' }}>{acc ? `${acc.account_code}  ${acc.account_name}` : l.description}</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: 600 }}>{l.debit_amount > 0 ? 'LKR ' + Number(l.debit_amount).toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}</td>
                      <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 600 }}>{l.credit_amount > 0 ? 'LKR ' + Number(l.credit_amount).toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!isClientRefund && !isLKR && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                Supplier receives {currency?.code} {parseFloat(form.amount || 0).toLocaleString()}.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
